import React, { useCallback, useEffect, useState } from 'react';
import { delegationService } from '../services/delegationService';
import { DelegationSummary, DelegationStatus } from '../types/delegation';
import { useConfirm } from './confirm/ConfirmDialogProvider';
import { notifyError, notifySuccess } from '../utils/notificationCenter';
import { InlineIcon } from './ui/InlineIcon';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Ban,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface DelegationSummaryCardProps {
  assemblyId: string;
}

const parseApartmentList = (apartmentValue?: string): string[] => {
  if (!apartmentValue) return [];
  return apartmentValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const splitTowerAndApartment = (apartmentValue: string): { tower: string; apartment: string } => {
  const separatorIndex = apartmentValue.indexOf('-');
  if (separatorIndex <= 0) {
    return { tower: '', apartment: apartmentValue };
  }

  return {
    tower: apartmentValue.slice(0, separatorIndex).trim(),
    apartment: apartmentValue.slice(separatorIndex + 1).trim(),
  };
};

const formatUnitLabel = (apartmentRaw: string): string => {
  const { tower, apartment } = splitTowerAndApartment(apartmentRaw);
  if (tower && apartment) {
    return `Torre ${tower} · Apt ${apartment}`;
  }
  if (apartment) {
    return `Apt ${apartment}`;
  }
  return apartmentRaw;
};

const UnitChips: React.FC<{ apartmentValue?: string }> = ({ apartmentValue }) => {
  const units = parseApartmentList(apartmentValue);

  if (units.length === 0) {
    return (
      <p className="mt-1.5 text-[11px] font-medium tracking-wide text-slate-500">
        Sin unidad registrada
      </p>
    );
  }

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" aria-label="Torre y apartamento">
      {units.map((unit) => (
        <span
          key={unit}
          className="inline-flex items-center rounded-md border border-emerald-200/80 bg-emerald-50/90 px-2 py-0.5 font-mono text-[11px] font-semibold tracking-tight text-emerald-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"
        >
          {formatUnitLabel(unit)}
        </span>
      ))}
    </div>
  );
};

const formatDelegatorUnitsSummary = (apartmentValue?: string): string => {
  const units = parseApartmentList(apartmentValue);
  if (units.length === 0) return '';
  return units.map(formatUnitLabel).join(' · ');
};

const DelegationSummaryCard: React.FC<DelegationSummaryCardProps> = ({ assemblyId }) => {
  const { confirm } = useConfirm();
  const [summary, setSummary] = useState<DelegationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [showDelegations, setShowDelegations] = useState(false);

  const loadSummary = useCallback(async () => {
    setError(null);
    try {
      const data = await delegationService.getMyDelegations(assemblyId);
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar delegaciones');
    } finally {
      setLoading(false);
    }
  }, [assemblyId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!summary) {
      setShowDelegations(false);
      return;
    }

    const total = summary.delegationsGiven.length + summary.delegationsReceived.length;
    if (total === 0) {
      setShowDelegations(false);
      return;
    }

    setShowDelegations(total <= 3);
  }, [summary]);

  const handleRevoke = async (delegationId: string) => {
    const shouldRevoke = await confirm({
      title: 'Revocar delegación',
      message: '¿Estás seguro de revocar esta delegación?',
      confirmText: 'Revocar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldRevoke) {
      return;
    }

    setRevoking(delegationId);
    try {
      await delegationService.revokeDelegation(delegationId);
      await loadSummary(); // Recargar
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al revocar');
    } finally {
      setRevoking(null);
    }
  };

  const handleCreateDelegation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!delegateEmail.trim()) {
      notifyError('Ingresa un email válido para delegar');
      return;
    }

    setCreating(true);
    try {
      await delegationService.createDelegationAuthenticated(assemblyId, delegateEmail);
      notifySuccess('Se envió la solicitud de delegación al correo indicado', 'Delegación enviada');
      setDelegateEmail('');
      await loadSummary();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al crear la delegación');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: DelegationStatus) => {
    const badges = {
      [DelegationStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [DelegationStatus.ACCEPTED]: 'bg-green-100 text-green-800',
      [DelegationStatus.REJECTED]: 'bg-red-100 text-red-800',
      [DelegationStatus.REVOKED]: 'bg-gray-100 text-gray-800',
    };

    const labels: Record<
      DelegationStatus,
      { icon: LucideIcon; label: string }
    > = {
      [DelegationStatus.PENDING]: { icon: Clock, label: 'Pendiente' },
      [DelegationStatus.ACCEPTED]: { icon: CheckCircle2, label: 'Aceptada' },
      [DelegationStatus.REJECTED]: { icon: XCircle, label: 'Rechazada' },
      [DelegationStatus.REVOKED]: { icon: Ban, label: 'Revocada' },
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        <span className="inline-flex items-center gap-1.5">
          <InlineIcon icon={labels[status].icon} className="text-current/90" />
          <span>{labels[status].label}</span>
        </span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)] md:p-6">
        <div className="animate-pulse">
          <div className="mb-4 h-4 w-1/3 rounded bg-slate-200"></div>
          <div className="space-y-3">
            <div className="h-3 rounded bg-slate-200"></div>
            <div className="h-3 w-5/6 rounded bg-slate-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)] md:p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const hasAnyDelegation = summary.delegationsGiven.length > 0 || summary.delegationsReceived.length > 0;
  const totalDelegations = summary.delegationsGiven.length + summary.delegationsReceived.length;
  const hasAcceptedGivenDelegation = summary.delegationsGiven.some((d) => d.status === DelegationStatus.ACCEPTED);
  const hasPendingGivenDelegation = summary.delegationsGiven.some((d) => d.status === DelegationStatus.PENDING);
  const hasReceivedDelegations = summary.delegationsReceived.length > 0;
  const canCreateDelegation = !hasAcceptedGivenDelegation && !hasPendingGivenDelegation && !hasReceivedDelegations;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)] md:p-6">
      <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
            Delegaciones de Voto
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona quién te representa y los votos que representas.
          </p>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-right">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">Peso total actual</p>
          <div className="font-mono text-xl font-bold text-blue-700 md:text-2xl">
            {Number(summary.totalVotingWeight).toFixed(10)}
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 md:p-5">
        <h4 className="mb-2 text-sm font-semibold text-indigo-900">Delegar mi voto</h4>
        <p className="mb-3 text-xs text-indigo-800 md:text-sm">
          Ingresa el correo del propietario que votará por ti. Recibirá un email para aceptar o rechazar la solicitud.
        </p>

        {!canCreateDelegation && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 md:text-sm">
            No puedes crear una nueva delegación en este momento. Si ya delegaste, primero revoca; si recibiste delegaciones, no puedes delegar para evitar cadenas.
          </div>
        )}

        <form onSubmit={handleCreateDelegation} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="email"
            value={delegateEmail}
            onChange={(e) => setDelegateEmail(e.target.value)}
            placeholder="correo@ejemplo.com"
            disabled={creating || !canCreateDelegation}
            className="w-full rounded-xl border border-indigo-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
          />
          <button
            type="submit"
            disabled={creating || !canCreateDelegation || !delegateEmail.trim()}
            className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[120px]"
          >
            {creating ? 'Enviando...' : 'Delegar y firmar'}
          </button>
        </form>
      </div>

      {!hasAnyDelegation && (
        <div className="mb-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          Aún no tienes delegaciones activas para esta asamblea.
        </div>
      )}

      {hasAnyDelegation && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowDelegations((prev) => !prev)}
            className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {showDelegations ? 'Ocultar delegaciones' : `Mostrar delegaciones (${totalDelegations})`}
          </button>
        </div>
      )}

      {showDelegations && (
        <>
          <div className="max-h-96 overflow-y-auto pr-1">
            {/* Delegaciones Dadas */}
            {summary.delegationsGiven.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 text-sm font-semibold text-slate-700">
                  Tu delegación:
                </h4>
                {summary.delegationsGiven.map((delegation) => (
                  <div key={delegation.id} className="mb-2 rounded-xl border border-orange-200 bg-orange-50 p-3 md:p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">
                          <span className="inline-flex items-center gap-2">
                            <InlineIcon icon={ArrowRight} className="text-orange-700" />
                            <span>
                              Delegaste a:{' '}
                              <strong>{delegation.delegate?.firstName} {delegation.delegate?.lastName}</strong>
                            </span>
                          </span>
                        </p>
                        <p className="text-xs text-slate-600 md:text-sm">
                          {delegation.delegate?.email}
                        </p>
                        <p className="mt-1 text-xs text-slate-600 md:text-sm">
                          Coeficiente: {Number(delegation.delegatedCoefficient).toFixed(10)}
                        </p>
                        <div className="mt-2">
                          {getStatusBadge(delegation.status)}
                        </div>
                      </div>
                      {delegation.status === DelegationStatus.ACCEPTED && (
                        <button
                          onClick={() => handleRevoke(delegation.id)}
                          disabled={revoking === delegation.id}
                          className="ml-2 rounded-lg bg-red-100 px-3 py-1 text-xs text-red-700 transition hover:bg-red-200 disabled:opacity-50"
                        >
                          {revoking === delegation.id ? 'Revocando...' : 'Revocar'}
                        </button>
                      )}
                    </div>
                    {delegation.status === DelegationStatus.ACCEPTED && (
                      <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2">
                        <p className="text-xs text-yellow-800">
                          <span className="inline-flex items-center gap-2">
                            <InlineIcon icon={AlertTriangle} className="text-yellow-700" />
                            <span>No podrás votar en esta asamblea</span>
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Delegaciones Recibidas */}
            {summary.delegationsReceived.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold text-slate-700">
                  Votos delegados a ti:
                </h4>
                {summary.delegationsReceived.map((delegation) => {
                  const delegatorName = `${delegation.delegator?.firstName || ''} ${delegation.delegator?.lastName || ''}`.trim();
                  const unitSummary = formatDelegatorUnitsSummary(delegation.delegator?.profile?.apartment);

                  return (
                    <div key={delegation.id} className="mb-2 rounded-xl border border-green-200 bg-green-50 p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900">
                            <span className="inline-flex items-center gap-2">
                              <InlineIcon icon={ArrowLeft} className="text-emerald-700" />
                              <span>
                                De: <strong>{delegatorName}</strong>
                              </span>
                            </span>
                          </p>
                          <UnitChips apartmentValue={delegation.delegator?.profile?.apartment} />
                          <p className="mt-1 text-xs text-slate-600 md:text-sm">
                            {delegation.delegator?.email}
                          </p>
                          <p className="mt-1 text-xs text-slate-600 md:text-sm">
                            Coeficiente: {Number(delegation.delegatedCoefficient).toFixed(10)}
                          </p>
                          <div className="mt-2">
                            {getStatusBadge(delegation.status)}
                          </div>
                        </div>
                      </div>
                      {delegation.status === DelegationStatus.ACCEPTED && (
                        <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
                          <p className="text-xs text-blue-800">
                            ✓ Tu voto representará a {delegatorName}
                            {unitSummary ? ` · ${unitSummary}` : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Total Weight */}
          {summary.totalVotingWeight > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">
                  Peso total de tu voto:
                </span>
                <span className="font-mono text-lg font-bold text-blue-600">
                  {Number(summary.totalVotingWeight).toFixed(10)}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Tu voto vale por {Number(summary.totalVotingWeight).toFixed(10)} {Number(summary.totalVotingWeight) === 1 ? 'persona' : 'personas'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DelegationSummaryCard;